<script lang="ts">
  import DataTable, { Head, Body, Row, Cell, Pagination, Label } from "@smui/data-table";
  import Select, { Option } from "@smui/select";
  import IconButton from "@smui/icon-button";
  import CircularProgress from "@smui/circular-progress";
  import { _ } from "svelte-i18n";

  import type { RpDemVsPurch } from "../../../types/Report";

  export let items = [];
  export let loadingData = false;
  export let dataLoaded = false;

  // Filters
  export let countrySearchArray = [];
  export let dcSearchArray = [];
  export let supplierSearchArray = [];
  export let buyerSearchArray = [];
  export let filteredItems: RpDemVsPurch[] = [];

  // Pagging
  let rowsPerPage = 100;
  let currentPage = 0;

  $: start = currentPage * rowsPerPage;
  $: end = Math.min(start + rowsPerPage, filteredItems.length);
  $: slice = filteredItems.slice(start, end);
  $: lastPage = Math.max(Math.ceil(filteredItems.length / rowsPerPage) - 1, 0);

  $: if (currentPage > lastPage) {
    currentPage = lastPage;
  }

  // Apply filters
  $: filteredItems = items?.filter((item) => {
    const countryMatch = countrySearchArray.length > 0 ? countrySearchArray.some((unitId) => item.countryCode?.includes(unitId)) : true;
    // Convert item.warehouseId to a DC code for comparison
    const dcMatch = dcSearchArray.length > 0 ? dcSearchArray.map((d) => d.id).includes(item.dcCode) : true;
    const supplierMatch =
      supplierSearchArray.length > 0
        ? supplierSearchArray
            .map((x) => {
              return x.id;
            })
            .includes(item.supplierId)
        : true;
    const buyerMatch =
      buyerSearchArray.length > 0
        ? buyerSearchArray
            .map((x) => {
              return x.label;
            })
            .includes(item.buyer)
        : true;

    return countryMatch && dcMatch && supplierMatch && buyerMatch;
  });

  $: {
    console.log("---------------------");
    console.log({ items });
  }
</script>

<DataTable table$aria-label="report" class="w-100 mt-2 report">
  <Head>
    <Row style="height: 4rem !important;">
      <Cell columnId="TescoYear" class="datatable-header no-border-radius text-center wrap-spaces" numeric>
        <Label>{$_("year")}</Label>
      </Cell>
      <Cell columnId="TescoWeek" class="datatable-header no-border-radius text-center wrap-spaces" numeric>
        <Label>{$_("week")}</Label>
      </Cell>
      <Cell columnId="CountryCode" class="datatable-header">
        <Label>{$_("country")}</Label>
      </Cell>
      <Cell columnId="DCCode" class="datatable-header">
        <Label>{$_("dc")}</Label>
      </Cell>
      <Cell columnId="ArticleGroup" class="datatable-header text-center wrap-spaces">
        <Label>{$_("article_group")}</Label>
      </Cell>
      <Cell columnId="Buyer" class="datatable-header">
        <Label>{$_("buyer")}</Label>
      </Cell>
      <Cell columnId="GFSTPN" class="datatable-header text-center wrap-spaces" >
        <Label>{$_("tpn_en")}</Label>
      </Cell>
      <Cell columnId="ProductName" class="datatable-header">
        <Label>{$_("product_en")}</Label>
      </Cell>
      <Cell columnId="TotalForecasted" class="datatable-header text-center wrap-spaces">
        <Label>{$_("total_forecasted")}</Label>
      </Cell>
      <Cell columnId="TotalPurchased" class="datatable-header text-center wrap-spaces">
        <Label>{$_("total_purchased")}</Label>
      </Cell>
      <Cell columnId="difference" class="datatable-header text-center wrap-spaces">
        <Label>{$_("difference")}</Label>
      </Cell>
      <Cell columnId="differencePerc" class="datatable-header text-center wrap-spaces">
        <Label>{$_("difference_perc")}</Label>
      </Cell>
      <Cell columnId="Status" class="datatable-header" >
        <Label>{$_("status")}</Label>
      </Cell>
      <Cell columnId="ReasonCode" class="datatable-header" >
        <Label>{$_("reason_code")}</Label>
      </Cell>
      <Cell columnId="caseAutoround" class="datatable-header text-center wrap-spaces" >
        <Label>{$_("case_autoround")}</Label>
      </Cell>
      <Cell columnId="countryAutoround" class="datatable-header text-center wrap-spaces" >
        <Label>{$_("country_autoround")}</Label>
      </Cell>
      <Cell columnId="Comment" class="datatable-header text-center wrap-spaces" >
        <Label>{$_("buyer_comment")}</Label>
      </Cell>
    </Row>
  </Head>
  <Body>
    {#if loadingData}
      <Row>
        <td colspan="15" class="mdc-data-table__cell" style="width: 100%; text-align: center;">
          <CircularProgress style="height: 32px; width: 32px; margin: auto" indeterminate />
        </td>
      </Row>
    {:else if slice && slice.length == 0}
      <Row>
        <td colspan="15" class="mdc-data-table__cell" style="width: 100%; text-align: center; height: 3rem !important;">
          {dataLoaded ? $_("no_data_for_week") : $_("load_week_data")}
        </td>
      </Row>
    {:else}
      {#each slice as data (data.id)}
        <Row>
          <Cell numeric>{data.tescoYear}</Cell>
          <Cell numeric>{data.tescoWeek}</Cell>
          <Cell class="text-center">{data.countryCode}</Cell>
          <Cell class="text-center">{data.dcCode}</Cell>
          <Cell>{data.articleGroup}</Cell>
          <Cell>{data.buyer}</Cell>
          <Cell  class="text-center">{data.productId}</Cell>
          <Cell>{data.ceTpnName}</Cell>
          <Cell numeric>{data.totalForecasted}</Cell>
          <Cell numeric>{data.totalPurchased}</Cell>
          <Cell numeric>{data.difference}</Cell>
          <Cell numeric>{data.differencePerc}</Cell>
          <Cell>{$_(data.status)}</Cell>
          <Cell>{data?.reasonCode? $_(data.reasonCode): ""}</Cell>
          <Cell class="text-center {$_((data.caseAutoround === 0)? "grey-text": "red-text")}">{$_((data.caseAutoround === 0)? "no": "yes")}</Cell>
          <Cell class="text-center {$_((data.countryAutoround === 0)? "grey-text": "red-text")}">{$_((data.countryAutoround === 0)? "no": "yes")}</Cell>
          <Cell>{data.comment? data.comment: ""}</Cell>
        </Row>
      {/each}
    {/if}
  </Body>

  <Pagination slot="paginate">
    <svelte:fragment slot="rowsPerPage">
      <Label>{$_("rows_per_page")}</Label>
      <Select variant="outlined" bind:value={rowsPerPage} noLabel>
        <Option value={100}>100</Option>
        <Option value={250}>250</Option>
        <Option value={1000}>1000</Option>
      </Select>
    </svelte:fragment>
    <svelte:fragment slot="total">
      {start + 1}-{end} of {filteredItems.length}
    </svelte:fragment>

    <IconButton class="material-icons" action="first-page" title="First page" on:click={() => (currentPage = 0)} disabled={currentPage === 0}>first_page</IconButton>
    <IconButton class="material-icons" action="prev-page" title="Prev page" on:click={() => currentPage--} disabled={currentPage === 0}>chevron_left</IconButton>
    <IconButton class="material-icons" action="next-page" title="Next page" on:click={() => currentPage++} disabled={currentPage === lastPage}>chevron_right</IconButton>
    <IconButton class="material-icons" action="last-page" title="Last page" on:click={() => (currentPage = lastPage)} disabled={currentPage === lastPage}>last_page</IconButton>
  </Pagination>
</DataTable>
